// Environment materials (§12.3, §12.5). Arena surfaces are the §12.3 toon (MeshToonMaterial on the
// shared 4-step ramp) patched with a procedural albedo, optional procedural glow, a toon specular
// "gloss" highlight and the fresnel rim in the map's sky-bottom color, so environment shading
// matches the characters while each map gets its own surface shader (cardboard fibres, porcelain
// bands, playfield inserts, sponge stripes, drum perforations). Static props share one
// vertex-colored toon material per map, which is what lets them merge into a single draw call.
import {
  BackSide, Color, DoubleSide, FrontSide, MeshBasicMaterial, MeshToonMaterial, ShaderMaterial, UniformsLib, UniformsUtils,
  type ColorRepresentation, type IUniform, type Material, type Side, type Texture,
} from 'three';
import { INK, OUTLINE_WIDTH_GLSL, outlinePxFor } from '../../materials/library';
import { GLSL_NOISE } from './glsl';

/** Uniforms every environment shader may read; one set per MapView, updated once per frame. */
export interface EnvUniforms {
  /** Seconds of presentation time (scaled by replay slow-motion). */
  uTime: IUniform<number>;
  /** Fractional beat of the round clock. */
  uBeat: IUniform<number>;
  /** 0..1 crowd excitement (§12.6). */
  uExcite: IUniform<number>;
  /** Fresnel rim tint: the map's sky-bottom color (§12.3). */
  uRimColor: IUniform<Color>;
}

export function createEnvUniforms(rim: ColorRepresentation): EnvUniforms {
  return { uTime: { value: 0 }, uBeat: { value: 0 }, uExcite: { value: 0 }, uRimColor: { value: new Color(rim) } };
}

export interface SurfaceOptions {
  /** Program cache key: one per distinct shader body. */
  key: string;
  color?: ColorRepresentation;
  vertexColors?: boolean;
  /** Fresnel rim strength (default 0.35, §12.3). */
  rim?: number;
  /** Toon specular highlight strength, 0..1. */
  gloss?: number;
  /** Highlight size: the N·H threshold (default 0.94; higher = tighter). */
  glossEdge?: number;
  /** GLSL statements that may rewrite `vec3 albedo`. In scope: envObj, envWorld, envNw (world normal), envNo (object normal), envUv, uniforms. */
  albedo?: string;
  /** GLSL statements that may add to `vec3 glow` (unlit, bloom-friendly). */
  emissive?: string;
  /** GLSL statements run after `begin_vertex`; may rewrite `transformed`. */
  vertex?: string;
  /** Extra declarations (uniforms, attributes, varyings) for the vertex shader. */
  vertexPars?: string;
  /** Extra declarations shared by both stages (uniforms, varyings, functions). */
  pars?: string;
  /** Statements at the end of the vertex shader (assign custom varyings). */
  vertexEnd?: string;
  uniforms?: Record<string, IUniform>;
  side?: Side;
  transparent?: boolean;
  opacity?: number;
  depthWrite?: boolean;
  map?: Texture | null;
}

const SURFACE_PARS = /* glsl */ `
uniform float uTime;
uniform float uBeat;
uniform float uExcite;
uniform vec3 uRimColor;
uniform float uRim;
uniform float uGloss;
uniform float uGlossEdge;
varying vec3 vEnvObj;
varying vec3 vEnvWorld;
varying vec3 vEnvNw;
varying vec3 vEnvNo;
varying vec2 vEnvUv;
`;

/** A §12.3 toon surface with a procedural albedo/glow; uniforms are shared by reference. */
export function surfaceMaterial(env: EnvUniforms, ramp: Texture, o: SurfaceOptions): MeshToonMaterial {
  const m = new MeshToonMaterial({
    color: o.color ?? '#FFFFFF', gradientMap: ramp, vertexColors: o.vertexColors ?? false,
    side: o.side ?? FrontSide, transparent: o.transparent ?? false, opacity: o.opacity ?? 1, map: o.map ?? null,
  });
  if (o.depthWrite !== undefined) m.depthWrite = o.depthWrite;
  const rim = { value: o.rim ?? 0.35 };
  const gloss = { value: o.gloss ?? 0 };
  const glossEdge = { value: o.glossEdge ?? 0.94 };
  const pars = SURFACE_PARS + GLSL_NOISE + declareUniforms(o.uniforms ?? {}, o.pars ?? '') + (o.pars ?? '');
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, env, o.uniforms ?? {}, { uRim: rim, uGloss: gloss, uGlossEdge: glossEdge });
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${pars}\n${o.vertexPars ?? ''}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${o.vertex ?? ''}`)
      .replace('#include <fog_vertex>', `#include <fog_vertex>
  {
    vec4 envW = vec4(transformed, 1.0);
    vec3 envN = objectNormal;
    #ifdef USE_INSTANCING
      envW = instanceMatrix * envW;
      envN = mat3(instanceMatrix) * envN;
    #endif
    envW = modelMatrix * envW;
    vEnvObj = transformed;
    vEnvWorld = envW.xyz;
    vEnvNw = normalize(mat3(modelMatrix) * envN);
    vEnvNo = objectNormal;
    vEnvUv = uv;
  }
  ${o.vertexEnd ?? ''}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${pars}`)
      .replace('#include <color_fragment>', `#include <color_fragment>
  {
    vec3 envObj = vEnvObj; vec3 envWorld = vEnvWorld; vec3 envNw = normalize(vEnvNw); vec3 envNo = normalize(vEnvNo); vec2 envUv = vEnvUv;
    vec3 albedo = diffuseColor.rgb;
    ${o.albedo ?? ''}
    diffuseColor.rgb = albedo;
  }`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  {
    vec3 envObj = vEnvObj; vec3 envWorld = vEnvWorld; vec3 envNw = normalize(vEnvNw); vec3 envNo = normalize(vEnvNo); vec2 envUv = vEnvUv;
    vec3 glow = vec3(0.0);
    ${o.emissive ?? ''}
    totalEmissiveRadiance += glow;
  }`)
      .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
  #if NUM_DIR_LIGHTS > 0
  if (uGloss > 0.0) {
    vec3 envH = normalize(directionalLights[0].direction + normalize(vViewPosition));
    float envSpec = smoothstep(uGlossEdge, uGlossEdge + 0.015, dot(normal, envH));
    reflectedLight.directDiffuse += envSpec * uGloss * directionalLights[0].color * 0.32;
  }
  #endif`)
      .replace('#include <opaque_fragment>', `outgoingLight += uRimColor * uRim * pow(1.0 - saturate(dot(normal, normalize(vViewPosition))), 3.0);
  #include <opaque_fragment>`);
  };
  m.customProgramCacheKey = () => `env-surface:${o.key}`;
  return m;
}

/** GLSL declarations for custom uniforms of simple types not already declared in `pars`. */
function declareUniforms(u: Record<string, IUniform>, pars: string): string {
  let out = '';
  for (const [name, { value }] of Object.entries(u)) {
    if (new RegExp(`\\b${name}\\b`).test(pars)) continue;
    let type = '';
    if (typeof value === 'number') type = 'float';
    else if (value && (value as Texture).isTexture) type = 'sampler2D';
    else if (value && (value as Color).isColor) type = 'vec3';
    else if (value && typeof value === 'object' && 'w' in value) type = 'vec4';
    else if (value && typeof value === 'object' && 'z' in value) type = 'vec3';
    else if (value && typeof value === 'object' && 'y' in value) type = 'vec2';
    if (type) out += `uniform ${type} ${name};\n`;
  }
  return out;
}

/** Unlit vertex-colored glow (neon, lamps, emissive windows); bright values feed bloom. */
export function glowMaterial(o: { vertexColors?: boolean; color?: ColorRepresentation; transparent?: boolean; opacity?: number; additive?: boolean } = {}): MeshBasicMaterial {
  const m = new MeshBasicMaterial({
    color: o.color ?? '#FFFFFF', vertexColors: o.vertexColors ?? true, toneMapped: false,
    transparent: o.transparent ?? false, opacity: o.opacity ?? 1,
  });
  if (o.additive) {
    m.blending = 2; // AdditiveBlending
    m.depthWrite = false;
    m.transparent = true;
  }
  return m;
}

const OUTLINE_VERTEX = /* glsl */ `
uniform float uWidth;
uniform float uScale;
#include <common>
#include <fog_pars_vertex>
${OUTLINE_WIDTH_GLSL}
void main() {
  vec3 pos = position;
  vec3 nrm = normal;
  #ifdef USE_INSTANCING
    pos = (instanceMatrix * vec4(pos, 1.0)).xyz;
    nrm = mat3(instanceMatrix) * nrm;
  #endif
  vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
  vec3 n = normalize(normalMatrix * nrm);
  mvPosition.xyz += n * so_outlineWidth(uWidth * uScale, -mvPosition.z);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;

const OUTLINE_FRAGMENT = /* glsl */ `
uniform vec3 uColor;
#include <common>
#include <fog_pars_fragment>
void main() {
  gl_FragColor = vec4(uColor, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

/**
 * The §12.3 inverted-hull outline, with instancing support (the library's hull is per mesh).
 * `scale` is the library's shared width multiplier when it exposes one (High-contrast outlines).
 */
export function outlineMaterial(width: number, scale: IUniform<number>, color: ColorRepresentation = INK): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: { ...UniformsUtils.clone(UniformsLib.fog), uWidth: { value: width }, uColor: { value: new Color(color) }, uScale: scale, uOutlinePx: outlinePxFor(scale) },
    vertexShader: OUTLINE_VERTEX,
    fragmentShader: OUTLINE_FRAGMENT,
    side: BackSide,
    fog: true,
  });
}

/** The outline-width multiplier the material library shares among its hulls, when it has one. */
export function sharedOutlineScale(libOutline: Material): IUniform<number> {
  const u = (libOutline as Partial<ShaderMaterial>).uniforms?.uScale as IUniform<number> | undefined;
  return u && typeof u.value === 'number' ? u : { value: 1 };
}

export { DoubleSide };
